// frontend/src/pages/AssessmentsPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import authAxios from '../auth/authAxios';
import { useMsal } from '@azure/msal-react';

interface AssessmentSummary {
  assessment_id: string;
  title: string;
  description: string;
  category: string;
  difficulty_level: string;
  time_limit_minutes: number;
  passing_score: number;
  question_count: number;
  is_completed: boolean;
  best_score?: number;
  attempts_count: number;

  // Resume functionality fields
  has_in_progress?: boolean;
  current_question_index?: number;
  total_questions?: number;
}

const AssessmentsPage: React.FC = () => {
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [userHasRole, setUserHasRole] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accounts } = useMsal();
  const user = accounts[0];
  const [hasRoleAssessment, setHasRoleAssessment] = useState(false);
  
  useEffect(() => {
        if (!user) {
          setHasRoleAssessment(false);
          return;
        }
  
        // Check if user has ChatGPT.Admin role (assuming roles in idToken claims)
        const roles = (user.idTokenClaims?.roles || []) as string[];
  
        if (roles.includes("ChatGPT.Assessment")) {
          setHasRoleAssessment(true);
        }
        else {
          setHasRoleAssessment(false);
        }
    }, [user]);
  useEffect(() => {
    fetchAvailableAssessments();
  }, []);

  const fetchAvailableAssessments = async () => {
    try {
      setLoading(true);

      // Check if user has completed their profile
      const profileResponse = await authAxios.get('/api/users/profile');
      const hasCompletedProfile = profileResponse.data.selected_role && profileResponse.data.profile_completed_at;
      setUserHasRole(hasCompletedProfile);

      // Get available assessments
      const response = await authAxios.get('/api/assessments/available');
      setAssessments(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'junior': return 'bg-green-100 text-green-800';
      case 'mid': return 'bg-yellow-100 text-yellow-800';
      case 'senior': return 'bg-orange-100 text-orange-800';
      case 'lead': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'frontend': return '🎨';
      case 'backend': return '⚙️';
      case 'security': return '🔒';
      case 'devops': return '🚀';
      case 'ai-automation': return '🤖';
      default: return '💻';
    }
  };

    if (user && !hasRoleAssessment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">You do not have access to this page.</div>
        </div>
      </div>
    );
  } 

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Skill Assessments</h1>
        <p className="text-gray-300">
          Test your development skills and get personalized feedback to improve your expertise.
        </p>
      </div>

      {assessments.length === 0 ? (
        <div className="text-center py-12">
          {!userHasRole ? (
            <>
              <div className="text-6xl mb-4">�</div>
              <h3 className="text-lg font-medium text-white mb-2">Select Your Role First</h3>
              <p className="text-gray-400 mb-6">
                Please select your role and language preferences to get personalized assessments.
              </p>
              <Link
                to="/role-selection"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors duration-200"
              >
                Select Your Role
              </Link>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-lg font-medium text-white mb-2">No Assessments Available</h3>
              <p className="text-gray-400 mb-6">
                There are currently no assessments available for your role and skill level.
                Check back later or contact your administrator.
              </p>
              <Link
                to="/role-selection"
                className="inline-flex items-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors duration-200"
              >
                Update Your Profile
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assessments.map((assessment) => (
            <div
              key={assessment.assessment_id}
              className="bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">
                      {getCategoryIcon(assessment.category)}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {assessment.title}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(
                          assessment.difficulty_level
                        )}`}
                      >
                        {assessment.difficulty_level}
                      </span>
                    </div>
                  </div>
                  {assessment.is_completed && (
                    <div className="text-green-400">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                  {assessment.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Time Limit:</span>
                    <span className="text-white">{assessment.time_limit_minutes} min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Status:</span>
                    <span className={`font-medium ${
                      assessment.is_completed
                        ? 'text-green-400'
                        : assessment.has_in_progress
                        ? 'text-yellow-400'
                        : 'text-gray-400'
                    }`}>
                      {assessment.is_completed
                        ? 'Completed'
                        : assessment.has_in_progress
                        ? `In Progress (${(assessment.current_question_index || 0) + 1}/${assessment.total_questions || 15})`
                        : 'Not Started'}
                    </span>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Link
                    to={`/assessments/${assessment.assessment_id}`}
                    className={`w-full text-white text-center py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                      assessment.has_in_progress
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {assessment.is_completed
                      ? 'Take Again'
                      : assessment.has_in_progress
                      ? `Resume Assessment (Question ${(assessment.current_question_index || 0) + 1})`
                      : 'Start Assessment'}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">How Assessments Work</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center">
            <div className="text-3xl mb-2">🎯</div>
            <h3 className="font-medium text-white mb-2">Skill-Based Questions</h3>
            <p className="text-gray-400 text-sm">
              Questions are tailored to your role and focus on real-world scenarios
            </p>
          </div>
          <div className="text-center">
            <div className="text-3xl mb-2">🤖</div>
            <h3 className="font-medium text-white mb-2">AI-Powered Analysis</h3>
            <p className="text-gray-400 text-sm">
              Get detailed feedback on your coding efficiency, security awareness, and more
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentsPage;
